import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

// ─────────────────────────────────────────────────────────────────────────────
// DEV ONLY: Set to true to bypass login when the backend is not running.
// Set back to false before committing or running against a real backend.
// ─────────────────────────────────────────────────────────────────────────────
const DEV_BYPASS_AUTH = false;

const DEV_MOCK_USER = {
  id: 'dev-user-001',
  email: 'dev@slim-erp.local',
  username: 'DevAdmin',
  roles: [{ code: 'SUPER_ADMIN', name: 'Super Admin', level: 1, isPrimary: true }],
  permissions: ['*:*:*'],
  profile: { firstName: 'Dev', lastName: 'Admin', department: 'Development', position: 'Developer' },
  isVerified: true,
};

// Initial state
const initialState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  REGISTER_START: 'REGISTER_START',
  REGISTER_SUCCESS: 'REGISTER_SUCCESS',
  REGISTER_FAILURE: 'REGISTER_FAILURE',
  TOKEN_REFRESH_SUCCESS: 'TOKEN_REFRESH_SUCCESS',
  TOKEN_REFRESH_FAILURE: 'TOKEN_REFRESH_FAILURE',
  UPDATE_USER: 'UPDATE_USER',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_LOADING: 'SET_LOADING'
};

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
    case AUTH_ACTIONS.REGISTER_START:
      return {
        ...state,
        isLoading: true,
        error: null
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };

    case AUTH_ACTIONS.REGISTER_SUCCESS:
      return {
        ...state,
        isLoading: false,
        error: null
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
    case AUTH_ACTIONS.REGISTER_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
        isLoading: false
      };

    case AUTH_ACTIONS.TOKEN_REFRESH_SUCCESS:
      return {
        ...state,
        token: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
        error: null
      };

    case AUTH_ACTIONS.TOKEN_REFRESH_FAILURE:
      return {
        ...initialState,
        isLoading: false,
        error: 'Session expired. Please login again.'
      };

    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    default:
      return state;
  }
};

// Safe fallback used when the context is read before AuthProvider has mounted
// (e.g. during Chrome tab-duplication's partial state restore).
// isLoading:true keeps ProtectedRoute in its LoadingSpinner state, and
// isAuthenticated:false ensures no protected content is ever shown.
const defaultAuthContext = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  login: async () => ({ success: false }),
  register: async () => ({ success: false }),
  logout: async () => {},
  forgotPassword: async () => ({ success: false }),
  resetPassword: async () => ({ success: false }),
  verifyEmail: async () => ({ success: false }),
  changePassword: async () => ({ success: false }),
  updateUser: () => {},
  clearError: () => {},
  hasRole: () => false,
  hasPermission: () => false,
  hasRoleLevel: () => false,
  getPrimaryRole: () => null,
  isAdmin: () => false,
  isManagerOrAdmin: () => false,
  isSupervisorOrAbove: () => false,
  canManageUsers: () => false,
  canCreateUsers: () => false,
  canViewReports: () => false,
  getToken: () => null,
  refreshTokens: async () => false,
};

// Create context with safe default so consumers never receive undefined
const AuthContext = createContext(defaultAuthContext);
const ACCESS_TOKEN_STORAGE_KEY = 'accessToken';
const SESSION_IDLE_TIMEOUT_KEY = 'sessionIdleTimeoutMs';
const DEFAULT_IDLE_TIMEOUT_MS = 60 * 60 * 1000;
const SESSION_EXTEND_THROTTLE_MS = 5 * 60 * 1000;
const IDLE_ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
// Renew the access token this long before it expires so authenticated requests
// never race a just-expired token (proactive refresh).
const PROACTIVE_REFRESH_BUFFER_MS = 60 * 1000;
// Fallback cadence when the access token's exp claim can't be decoded.
const PROACTIVE_REFRESH_FALLBACK_MS = 10 * 60 * 1000;

const throttle = (fn, waitMs) => {
  let lastCall = 0;
  return (...args) => {
    const now = Date.now();
    if (now - lastCall >= waitMs) {
      lastCall = now;
      fn(...args);
    }
  };
};

// Auth Provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const idleTimerRef = useRef(null);
  const lastServerExtendRef = useRef(0);
  const activityHandlerRef = useRef(null);
  const cleanupIdleMonitoringRef = useRef(null);
  const proactiveRefreshTimerRef = useRef(null);
  // Guards against rescheduling refresh after logout / teardown (avoids 401 spam loops).
  const idleMonitoringActiveRef = useRef(false);

  // Check for existing session on mount
  useEffect(() => {
    // DEV BYPASS: skip all API calls and inject a mock admin user
    if (DEV_BYPASS_AUTH) {
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user: DEV_MOCK_USER, token: 'dev-token', refreshToken: 'dev-refresh-token' },
      });
      return;
    }

    // Skip auth check only for truly public pages (application forms, payment pages, etc.)
    // Auth-related pages (/login, /register, etc.) still run initializeAuth so that users
    // with a valid refresh token cookie are silently redirected to the dashboard.
    const trulyPublicPaths = [
      '/forgot-password', '/reset-password', '/verify-email',
      '/apply/public', '/apply/token', '/submit-documents', '/scholarship-proof', '/membership/submit-documents',
      '/payment/submit', '/public/workshop-payment', '/pay/enrollment', '/processing-fee',
      '/public/event-proforma-payment',
      '/public/event-sponsorship-payment',
      '/public/sponsor-contract-payment',
      '/public/direct-payment',
      '/centers/register',
      '/centers/settlement-pay',
    ];
    const isTrulyPublicPath = trulyPublicPaths.some(path => window.location.pathname.startsWith(path));
    
    if (!isTrulyPublicPath) {
      initializeAuth();
    } else {
      // On truly public pages, just set loading to false
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tear down idle listeners when provider unmounts
  useEffect(() => () => teardownIdleMonitoring(), []);

  // Set up token refresh timer (currently handled by interceptor, kept for future enhancements)
  useEffect(() => {
    if (state.token) {
      setupTokenRefresh();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.token]);

  // Listen for global auth expired events dispatched by axios interceptor
  useEffect(() => {
    const handleAuthExpired = () => {
      teardownIdleMonitoring();
      clearAuthData();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      toast.error('Your session has expired. Please log in again.');
    };
    window.addEventListener('auth:expired', handleAuthExpired);
    return () => window.removeEventListener('auth:expired', handleAuthExpired);
  }, []);

  // Reset idle timer when the server session is extended (API activity or throttled heartbeat)
  useEffect(() => {
    const handleSessionExtended = (event) => {
      const idleMs = event.detail?.sessionIdleTimeoutMs;
      if (idleMs) {
        localStorage.setItem(SESSION_IDLE_TIMEOUT_KEY, idleMs.toString());
      }
      resetIdleTimer(true);
      // A fresh access token was just stored — realign the proactive timer to it.
      if (proactiveRefreshTimerRef.current) {
        scheduleProactiveRefresh();
      }
    };
    window.addEventListener('auth:session-extended', handleSessionExtended);
    return () => window.removeEventListener('auth:session-extended', handleSessionExtended);
  }, []);

  // Keep idle timers in sync across browser tabs
  useEffect(() => {
    const handleCrossTabActivity = (event) => {
      if (event.key !== 'lastActivityAt' || !event.newValue) return;
      const remaining = getRemainingIdleMs();
      if (remaining <= 0) {
        handleIdleTimeout();
        return;
      }
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(handleIdleTimeout, remaining);
    };
    window.addEventListener('storage', handleCrossTabActivity);
    return () => window.removeEventListener('storage', handleCrossTabActivity);
  }, []);

  const getIdleTimeoutMs = () => {
    const stored = parseInt(localStorage.getItem(SESSION_IDLE_TIMEOUT_KEY) || '0', 10);
    return stored > 0 ? stored : DEFAULT_IDLE_TIMEOUT_MS;
  };

  const getRemainingIdleMs = () => {
    const lastActivity = parseInt(localStorage.getItem('lastActivityAt') || '0', 10);
    const idleMs = getIdleTimeoutMs();
    if (!lastActivity) return idleMs;
    return idleMs - (Date.now() - lastActivity);
  };

  const isSessionIdleExpired = () => {
    const lastActivity = parseInt(localStorage.getItem('lastActivityAt') || '0', 10);
    if (!lastActivity) return false;
    return getRemainingIdleMs() <= 0;
  };

  const handleIdleTimeout = async () => {
    console.warn('[auth] session ended: idle timeout', {
      idleLimitMs: getIdleTimeoutMs(),
      idleForMs: Date.now() - parseInt(localStorage.getItem('lastActivityAt') || '0', 10),
    });
    try {
      await authAPI.logout({ reason: 'SESSION_EXPIRED' });
    } catch {
      // Ignore — session logout proceeds regardless
    }
    teardownIdleMonitoring();
    clearAuthData();
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
    toast.error('Your session has expired due to inactivity. Please log in again.');
  };

  const resetIdleTimer = (isNewActivity = true) => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

    if (isNewActivity) {
      localStorage.setItem('lastActivityAt', Date.now().toString());
      idleTimerRef.current = setTimeout(handleIdleTimeout, getIdleTimeoutMs());
      return;
    }

    const remaining = getRemainingIdleMs();
    if (remaining <= 0) {
      handleIdleTimeout();
      return;
    }
    idleTimerRef.current = setTimeout(handleIdleTimeout, remaining);
  };

  const extendServerSession = () => {
    if (!idleMonitoringActiveRef.current) return;
    const now = Date.now();
    if (now - lastServerExtendRef.current < SESSION_EXTEND_THROTTLE_MS) return;
    lastServerExtendRef.current = now;
    authAPI.refreshToken()
      .then((response) => {
        if (!idleMonitoringActiveRef.current) return;
        const data = response.data?.data || {};
        // Persist the rotated access token so an active user never hits an
        // avoidable 401 once their previous 15-minute access token expires.
        if (data.accessToken) {
          sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, data.accessToken);
          localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, data.accessToken);
        }
        const idleMs = data.sessionIdleTimeoutMs;
        if (idleMs) {
          localStorage.setItem(SESSION_IDLE_TIMEOUT_KEY, idleMs.toString());
        }
        window.dispatchEvent(new CustomEvent('auth:session-extended', {
          detail: { sessionIdleTimeoutMs: idleMs }
        }));
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 400 || status === 401 || status === 403 || status === 429) {
          console.warn('[auth] session ended: activity refresh rejected', { status });
          teardownIdleMonitoring();
          clearAuthData();
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      });
  };

  // ── Proactive access-token refresh ─────────────────────────────────────────
  // Keeps the short-lived (15 min) access token fresh by renewing it shortly
  // before it expires, independent of user activity. Crucially it uses
  // extendSession:false so it does NOT slide the inactivity window — the window
  // only advances on real user activity, so an idle user still logs out on time.
  const getAccessTokenExpiryMs = () => {
    const token = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) || sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
    if (!token) return 0;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp ? payload.exp * 1000 : 0;
    } catch {
      return 0;
    }
  };

  const clearProactiveRefresh = () => {
    if (proactiveRefreshTimerRef.current) {
      clearTimeout(proactiveRefreshTimerRef.current);
      proactiveRefreshTimerRef.current = null;
    }
  };

  const runProactiveRefresh = async () => {
    if (!idleMonitoringActiveRef.current) return;

    // If the inactivity window already elapsed, log out instead of refreshing.
    if (isSessionIdleExpired()) {
      handleIdleTimeout();
      return;
    }
    let shouldReschedule = true;
    try {
      const response = await authAPI.refreshToken({ extendSession: false });
      const data = response.data?.data || {};
      if (data.accessToken) {
        sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, data.accessToken);
        localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, data.accessToken);
      }
      if (data.sessionIdleTimeoutMs) {
        localStorage.setItem(SESSION_IDLE_TIMEOUT_KEY, data.sessionIdleTimeoutMs.toString());
      }
    } catch (err) {
      const status = err?.response?.status;
      // Fatal refresh outcomes — stop immediately.
      // 401/403: cookie invalid/expired
      // 400: no refresh cookie
      // 429: rate-limited after the old tight retry loop flooded this endpoint
      if (status === 400 || status === 401 || status === 403 || status === 429) {
        console.warn('[auth] session ended: proactive refresh rejected', { status });
        shouldReschedule = false;
        teardownIdleMonitoring();
        clearAuthData();
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
        toast.error(
          status === 429
            ? 'Too many session refresh attempts. Please wait a few minutes, then log in again.'
            : 'Your session has expired. Please log in again.'
        );
        return;
      }
      // Transient network failure — retry on the normal cadence below.
    } finally {
      if (shouldReschedule && idleMonitoringActiveRef.current) {
        scheduleProactiveRefresh();
      }
    }
  };

  const scheduleProactiveRefresh = () => {
    if (!idleMonitoringActiveRef.current) return;
    clearProactiveRefresh();
    const expiryMs = getAccessTokenExpiryMs();
    let delay = expiryMs
      ? expiryMs - Date.now() - PROACTIVE_REFRESH_BUFFER_MS
      : PROACTIVE_REFRESH_FALLBACK_MS;
    // Never schedule an immediate tight loop when the token is already past expiry.
    if (delay < 5_000) delay = 5_000;
    proactiveRefreshTimerRef.current = setTimeout(runProactiveRefresh, delay);
  };

  const handleUserActivity = () => {
    resetIdleTimer(true);
    extendServerSession();
  };

  const teardownIdleMonitoring = () => {
    idleMonitoringActiveRef.current = false;
    clearProactiveRefresh();
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (activityHandlerRef.current) {
      IDLE_ACTIVITY_EVENTS.forEach((evt) => {
        window.removeEventListener(evt, activityHandlerRef.current);
      });
      activityHandlerRef.current = null;
    }
    cleanupIdleMonitoringRef.current = null;
  };

  const setupIdleSessionMonitoring = (idleTimeoutMs, { resume = false } = {}) => {
    teardownIdleMonitoring();
    idleMonitoringActiveRef.current = true;
    if (idleTimeoutMs) {
      localStorage.setItem(SESSION_IDLE_TIMEOUT_KEY, idleTimeoutMs.toString());
    }
    // On a fresh login the refresh cookie was just issued, so throttle the next
    // heartbeat. On resume (e.g. browser reopened mid-window) allow the very first
    // user activity to immediately slide the server session forward.
    lastServerExtendRef.current = resume ? 0 : Date.now();
    resetIdleTimer(!resume);

    const throttledActivity = throttle(handleUserActivity, 1000);
    activityHandlerRef.current = throttledActivity;
    IDLE_ACTIVITY_EVENTS.forEach((evt) => {
      window.addEventListener(evt, throttledActivity, { passive: true });
    });
    cleanupIdleMonitoringRef.current = teardownIdleMonitoring;

    // Begin proactively renewing the access token before it expires.
    scheduleProactiveRefresh();
  };

  /**
   * Initialize authentication from stored tokens
   */
  const initializeAuth = async () => {
    if (isSessionIdleExpired()) {
      try {
        await authAPI.logout({ reason: 'SESSION_EXPIRED' });
      } catch {
        // Ignore — local cleanup proceeds regardless
      }
      clearAuthData();
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      return;
    }

    // ─── Helper: fetch a fresh access token via the HTTP-only refresh cookie ────
    const refreshViaCookie = async () => {
      const refreshResponse = await authAPI.refreshToken({ extendSession: false });
      const freshToken = refreshResponse.data.data.accessToken;
      const idleMs = refreshResponse.data.data.sessionIdleTimeoutMs;
      if (idleMs) {
        localStorage.setItem(SESSION_IDLE_TIMEOUT_KEY, idleMs.toString());
      }
      sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, freshToken);
      localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, freshToken);
      return freshToken;
    };

    // ─── Helper: decode JWT exp claim locally (no signature check) ─────────────
    const isTokenExpiredLocally = (token) => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return !payload.exp || payload.exp * 1000 < Date.now();
      } catch {
        return true;
      }
    };

    // ─── Helper: fetch the user profile, returns null on 401 (expired token) ───
    // getProfile calls /auth/me which the Axios 401 interceptor skips (auth URL),
    // so we catch 401 explicitly here rather than relying on the interceptor retry.
    const fetchProfile = async () => {
      try {
        const response = await authAPI.getProfile();
        return response.data.data.user;
      } catch (err) {
        if (err?.response?.status === 401) return null; // expired – caller will refresh
        throw err; // non-401 errors bubble up
      }
    };

    try {
      const sharedAccessToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
      let accessToken = sharedAccessToken || sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
      let userData = null;

      // Keep duplicated tabs aligned with the currently active browser session.
      if (accessToken) {
        sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
      }

      // ── Phase 1: try the token already in storage (skip if locally expired) ────
      if (accessToken) {
        if (isTokenExpiredLocally(accessToken)) {
          // Discard immediately without a network round-trip — go to Phase 2
          sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
          localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
          accessToken = null;
        } else {
          userData = await fetchProfile();
          if (!userData) {
            // Server rejected token — discard and fall through to Phase 2
            sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
            localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
            accessToken = null;
          }
        }
      }

      // ── Phase 2: no valid token yet → refresh via HTTP-only cookie ───────────
      if (!userData) {
        try {
          accessToken = await refreshViaCookie();
          userData = await fetchProfile();
          if (!userData) throw new Error('Profile unavailable after refresh');
        } catch {
          // No valid cookie → user must log in
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
          return;
        }
      }

      // ── Phase 3: guard against Chrome tab-duplication stale-token problem ────
      // localStorage.currentUserEmail is written on every login and cleared on
      // logout – it is shared across all tabs and always reflects who is really
      // logged in. If the profile we got came from a stale token (e.g. a previous
      // user's session copied into sessionStorage by Chrome's tab duplication),
      // discard it and re-authenticate via the HTTP-only cookie, which always
      // belongs to whoever most recently logged in on this browser.
      const expectedEmail = (localStorage.getItem('currentUserEmail') || '').trim().toLowerCase();
      let resolvedEmail = (userData?.email || userData?.profile?.email || '').trim().toLowerCase();

      if (expectedEmail && resolvedEmail && expectedEmail !== resolvedEmail) {
        // Stale token detected – get a fresh one from the cookie
        sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
        try {
          accessToken = await refreshViaCookie();
          userData = await fetchProfile();
          if (!userData) throw new Error('Profile unavailable after stale-token refresh');
          resolvedEmail = (userData?.email || userData?.profile?.email || '').trim().toLowerCase();

          if (expectedEmail && resolvedEmail && expectedEmail !== resolvedEmail) {
            // Even after refresh the user differs – genuine contamination, force login
            sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
            localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
            localStorage.removeItem('userPermissions');
            dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
            return;
          }
        } catch {
          sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
          localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
          localStorage.removeItem('userPermissions');
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
          return;
        }
      }

      // ── Authenticated ─────────────────────────────────────────────────────────
      sessionStorage.setItem('sessionUserId', resolvedEmail);
      if (userData.permissions) {
        localStorage.setItem('userPermissions', JSON.stringify(userData.permissions));
      }
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user: userData, token: accessToken, refreshToken: 'stored-in-httponly-cookie' }
      });

      const idleMs = parseInt(localStorage.getItem(SESSION_IDLE_TIMEOUT_KEY) || '0', 10) || DEFAULT_IDLE_TIMEOUT_MS;
      setupIdleSessionMonitoring(idleMs, { resume: true });

      // Fetch employee data in parallel (non-blocking) if user has email
      if (userData.email) {
        import('../services/employeeApi').then(({ default: employeeApi }) => {
          employeeApi.getEmployeeByEmail(userData.email)
            .then(empResponse => {
              if (empResponse.data.success && empResponse.data.data) {
                const employeeData = empResponse.data.data;
                dispatch({
                  type: AUTH_ACTIONS.UPDATE_USER,
                  payload: {
                    profile: {
                      ...userData.profile,
                      _id: employeeData._id,
                      employeeId: employeeData.employeeId,
                      firstName: employeeData.firstName,
                      lastName: employeeData.lastName,
                      displayName: employeeData.displayName,
                      department: employeeData.departmentId?.departmentName || userData.profile?.department,
                      position: employeeData.positionId?.designationName || userData.profile?.position,
                      profilePhoto: employeeData.profilePhoto,
                      pendingProfilePhoto: employeeData.pendingProfilePhoto
                    }
                  }
                });
              }
            })
            .catch(() => {
              // User may not be an employee - this is not an error
            });
        });
      }
    } catch (error) {
      console.error('Auth initialization error:', error?.response?.status, error?.message);
      sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
      localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  /**
   * Login user
   */
  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const response = await authAPI.login(credentials);
      
      const { user, accessToken, sessionIdleTimeoutMs } = response.data.data;

      // Store access token in session storage (clears on browser close)
      sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
      localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
      // Stamp the authenticated user's email in localStorage (shared, real-time) and
      // sessionStorage (tab-local). Email is a stable plain-string identifier that is
      // always present and never has ObjectId vs string type ambiguity.
      const userEmail = (user.email || user.profile?.email || '').trim().toLowerCase();
      localStorage.setItem('currentUserEmail', userEmail);
      sessionStorage.setItem('sessionUserId', userEmail);
      
      // Store user permissions in localStorage for quick client-side checks
      if (user.permissions) {
        localStorage.setItem('userPermissions', JSON.stringify(user.permissions));
      }

      // Start idle session monitoring
      if (sessionIdleTimeoutMs) {
        setupIdleSessionMonitoring(sessionIdleTimeoutMs);
      }

      // Dispatch login success immediately
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user,
          token: accessToken,
          refreshToken: 'stored-in-httponly-cookie' // Refresh token in HTTP-only cookie
        }
      });

      // Fetch employee data in parallel (non-blocking) if user has email
      if (user.email) {
        import('../services/employeeApi').then(({ default: employeeApi }) => {
          employeeApi.getEmployeeByEmail(user.email)
            .then(empResponse => {
              if (empResponse.data.success && empResponse.data.data) {
                const employeeData = empResponse.data.data;
                // Update user profile with employee data
                dispatch({
                  type: AUTH_ACTIONS.UPDATE_USER,
                  payload: {
                    profile: {
                      ...user.profile,
                      _id: employeeData._id,
                      employeeId: employeeData.employeeId,
                      firstName: employeeData.firstName,
                      lastName: employeeData.lastName,
                      displayName: employeeData.displayName,
                      department: employeeData.departmentId?.departmentName || user.profile?.department,
                      position: employeeData.positionId?.designationName || user.profile?.position,
                      profilePhoto: employeeData.profilePhoto,
                      pendingProfilePhoto: employeeData.pendingProfilePhoto
                    }
                  }
                });
              }
            })
            .catch(() => {
              // User may not be an employee - this is not an error
              console.log('User is not an employee or employee data unavailable');
            });
        });
      }

      toast.success('Login successful!');
      return { success: true };

    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage
      });

      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Register user
   */
  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);

      toast.success('Registration successful! Please check your email to verify your account.');
      return { success: true, data: response.data.data };

    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed';
      const fieldErrors = error.response?.data?.errors;

      // Prefer showing a specific field validation message if available
      let toastMessage = errorMessage;
      if (error.response?.status === 400 && Array.isArray(fieldErrors) && fieldErrors[0]?.message) {
        toastMessage = fieldErrors[0].message;
      }

      toast.error(toastMessage);
      return { success: false, error: errorMessage, errors: Array.isArray(fieldErrors) ? fieldErrors : undefined };
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      // Set flag to indicate this is an explicit logout (not session expiry)
      sessionStorage.setItem('explicitLogout', 'true');
      
      // Call logout API to invalidate token on server and clear cookies
      await authAPI.logout();
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      clearAuthData();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      toast.success('Logged out successfully');
    }
  };

  /**
   * Refresh tokens
   */
  const refreshTokens = async () => {
    try {
      // Refresh token is in HTTP-only cookie, new access token returned in response
      const response = await authAPI.refreshToken();
      const { accessToken, sessionIdleTimeoutMs } = response.data.data;
      
      // Store new access token in session storage
      sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
      localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);

      if (sessionIdleTimeoutMs) {
        localStorage.setItem(SESSION_IDLE_TIMEOUT_KEY, sessionIdleTimeoutMs.toString());
      }
      
      dispatch({
        type: AUTH_ACTIONS.TOKEN_REFRESH_SUCCESS,
        payload: {
          accessToken: accessToken,
          refreshToken: 'stored-in-httponly-cookie'
        }
      });

      window.dispatchEvent(new CustomEvent('auth:session-extended', {
        detail: { sessionIdleTimeoutMs }
      }));

      return true;

    } catch (error) {
      console.error('Token refresh error:', error);
      sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
      localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
      clearAuthData();
      dispatch({ type: AUTH_ACTIONS.TOKEN_REFRESH_FAILURE });
      throw error;
    }
  };

  /**
   * Setup automatic token refresh
   */
  const setupTokenRefresh = () => {
    // Token refresh is now handled automatically by the backend via HTTP-only cookies
    // The axios interceptor will trigger refresh when it gets a 401 response
    // No need for client-side timer anymore
  };

  /**
   * Clear authentication data
   */
  const clearAuthData = () => {
    teardownIdleMonitoring();
    sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem('sessionUserId');
    localStorage.removeItem('currentUserEmail');
    localStorage.removeItem('currentUserId'); // legacy key – remove if present
    localStorage.removeItem('userPermissions');
    localStorage.removeItem(SESSION_IDLE_TIMEOUT_KEY);
    localStorage.removeItem('sessionExpiresAt'); // legacy key
    localStorage.removeItem('lastActivityAt');
    // HTTP-only refresh token cookie is cleared by the backend on logout
  };

  /**
   * Forgot password
   */
  const forgotPassword = async (email) => {
    try {
      const response = await authAPI.forgotPassword(email);
      toast.success('Password reset instructions sent to your email');
      return { success: true, data: response.data.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to send reset email';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Reset password
   */
  const resetPassword = async (token, password, confirmPassword) => {
    try {
      const response = await authAPI.resetPassword({
        token,
        password,
        confirmPassword
      });
      
      toast.success('Password reset successful! You can now login with your new password.');
      return { success: true, data: response.data.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Password reset failed';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Verify email
   */
  const verifyEmail = async (token) => {
    try {
      const response = await authAPI.verifyEmail(token);
      toast.success('Email verified successfully! Your account is now active.');
      return { success: true, data: response.data.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Email verification failed';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Change password
   */
  const changePassword = async (passwordData) => {
    try {
      const response = await authAPI.changePassword(passwordData);

      toast.success('Password changed successfully!');
      return { success: true, data: response.data.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Password change failed';
      // Don't show toast here, let the component handle it
      return { success: false, error: errorMessage, response: error.response };
    }
  };

  /**
   * Update user profile
   */
  const updateUser = (userData) => {
    dispatch({
      type: AUTH_ACTIONS.UPDATE_USER,
      payload: userData
    });
  };

  /**
   * Clear error
   */
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  /**
   * Check if user has specific role
   */
  const hasRole = (role) => {
    if (!state.user) return false;
    
    const targetRoles = (Array.isArray(role) ? role : [role]).map((r) =>
      String(r || '').toUpperCase()
    ).filter(Boolean);

    if (targetRoles.length === 0) return false;

    // Check direct user.role property
    if (state.user.role && targetRoles.includes(String(state.user.role).toUpperCase())) {
      return true;
    }

    // Check user.primaryRole (string or object)
    const primaryCode = typeof state.user.primaryRole === 'string'
      ? state.user.primaryRole
      : state.user.primaryRole?.roleCode || state.user.primaryRole?.code || state.user.primaryRole?.name;
    if (primaryCode && targetRoles.includes(String(primaryCode).toUpperCase())) {
      return true;
    }

    // Check user.roles array
    if (state.user.roles && Array.isArray(state.user.roles)) {
      const userRoleCodes = state.user.roles.map((r) => {
        if (typeof r === 'string') return r.toUpperCase();
        return String(r.roleCode || r.code || r.name || '').toUpperCase();
      }).filter(Boolean);
      return targetRoles.some((r) => userRoleCodes.includes(r));
    }
    
    return false;
  };

  /**
   * Check if user has SUPER_ADMIN role
   */
  const isSuperAdmin = () => hasRole('SUPER_ADMIN');

  /**
   * Check if user has ADMIN role but is not SUPER_ADMIN
   */
  const isOnlyAdmin = () => hasRole('ADMIN') && !isSuperAdmin();

  /**
   * Helper to check if a permission code is HRM-related
   */
  const isHrmPermissionCode = (perm) => {
    const permUpper = String(typeof perm === 'string' ? perm : perm?.code || '').toUpperCase();
    return permUpper.startsWith('HRM:') || permUpper.startsWith('HR:');
  };

  /**
   * Check if user has specific permission (with wildcard support)
   */
  const hasPermission = (permission) => {
    if (!state.user) return false;

    // SUPER_ADMIN automatically has access to all permissions
    if (isSuperAdmin()) {
      return true;
    }

    // ADMIN automatically has access to all non-HRM permissions
    if (hasRole('ADMIN')) {
      if (!permission) return true;
      if (Array.isArray(permission)) {
        if (permission.length === 0) return true;
        // User has access if there is at least one non-HRM permission
        return permission.some((p) => !isHrmPermissionCode(p));
      }
      return !isHrmPermissionCode(permission);
    }
    
    // Check if user has permissions array
    if (!state.user.permissions || !Array.isArray(state.user.permissions)) {
      return false;
    }
    
    const userPermissions = state.user.permissions
      .map((p) => (typeof p === 'string' ? p : p && p.code))
      .filter((p) => typeof p === 'string' && p.length > 0);
    
    /**
     * Check single permission with wildcard support
     */
    const checkSinglePermission = (perm) => {
      const permUpper = perm.toUpperCase();
      
      // Exact match
      if (userPermissions.some(userPerm => 
        userPerm.toUpperCase() === permUpper
      )) {
        return true;
      }

      // T&W: dedicated CRM:TW_<RESOURCE>:<ACTION> codes only (no TW / CRM:* shortcuts)
      if (permUpper.startsWith('CRM:TW_')) {
        return userPermissions.some((userPerm) => userPerm === '*:*:*');
      }
      
      // Wildcard support: SERVICE:* or SERVICE:RESOURCE:*
      const [service, resource] = permUpper.split(':');
      
      // Check for service-level wildcard (e.g., FINANCE:*)
      const serviceWildcard = `${service}:*`;
      if (userPermissions.some(userPerm => 
        userPerm.toUpperCase() === serviceWildcard
      )) {
        return true;
      }

      // Check for resource-level wildcard (e.g., FINANCE:BILLS:*)
      if (resource) {
        const resourceWildcard = `${service}:${resource}:*`;
        if (userPermissions.some(userPerm => 
          userPerm.toUpperCase() === resourceWildcard
        )) {
          return true;
        }
      }

      // Module-level "all resources, all actions" (e.g. EVENTS:*:*)
      if (service && permUpper.startsWith(`${service}:`)) {
        const moduleAll = `${service}:*:*`;
        if (userPermissions.some((userPerm) => userPerm.toUpperCase() === moduleAll)) {
          return true;
        }
      }
      
      // Check for super admin wildcard
      if (userPermissions.some(userPerm => userPerm === '*:*:*')) {
        return true;
      }
      
      return false;
    };
    
    // Handle array of permissions (OR logic - user needs at least one)
    if (Array.isArray(permission)) {
      return permission.some(perm => checkSinglePermission(perm));
    }
    
    // Handle single permission
    return checkSinglePermission(permission);
  };

  /**
   * Check if user has minimum role level
   */
  const hasRoleLevel = (minLevel) => {
    if (!state.user || !state.user.roles) return false;
    
    return state.user.roles.some(role => role.level <= minLevel);
  };

  /**
   * Get user's primary role
   */
  const getPrimaryRole = () => {
    if (!state.user) return null;
    
    if (state.user.primaryRole) return state.user.primaryRole;
    if (state.user.roles && state.user.roles.length > 0) {
      return state.user.roles.find(r => r.isPrimary) || state.user.roles[0];
    }
    
    return null;
  };

  /**
   * Check if user is admin (Level 1-2)
   */
  const isAdmin = () => hasRole(['SUPER_ADMIN', 'ADMIN']);

  /**
   * Check if user is manager or admin (Level 1-3)
   */
  const isManagerOrAdmin = () => hasRole(['SUPER_ADMIN', 'ADMIN', 'MANAGER']);

  /**
   * Check if user is supervisor or above (Level 1-4)
   */
  const isSupervisorOrAbove = () => hasRole(['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'SUPERVISOR']);

  /**
   * Check if user can create user accounts
   */
  const canCreateUsers = () => {
    return hasPermission(['USERS:PROFILES:CREATE']) || isAdmin();
  };

  /**
   * Check if user can manage other users (update profiles, roles, etc.)
   */
  const canManageUsers = () => {
    return hasPermission(['USERS:PROFILES:MANAGE', 'USERS:ROLES:ASSIGN']) || isAdmin();
  };

  /**
   * Check if user can view reports
   */
  const canViewReports = () => {
    return hasPermission([
      'REPORTS:SALES:READ',
      'REPORTS:INVENTORY:READ', 
      'REPORTS:FINANCIAL:READ'
    ]) || isManagerOrAdmin();
  };

  /**
   * Get current token
   */
  const getToken = () => state.token;

  const value = {
    // State
    ...state,

    // Actions
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    changePassword,
    updateUser,
    clearError,
    
    // Utilities
    hasRole,
    isSuperAdmin,
    isOnlyAdmin,
    isAdminOnly: isOnlyAdmin(),
    hasPermission,
    hasRoleLevel,
    getPrimaryRole,
    isAdmin,
    isManagerOrAdmin,
    isSupervisorOrAbove,
    canManageUsers,
    canCreateUsers,
    canViewReports,
    getToken,
    refreshTokens
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => useContext(AuthContext);

export default AuthContext;